import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '@/domains/survey';
import { adminApi } from '../api/admin-api';
import { definitionsApi } from '../api/definitions-api';
import { oversightApi } from '../api/oversight-api';
import type {
  DefinitionVersionStatus,
  IDefinitionSurvey,
  IDefinitionVersion,
  IVersionDocument,
  VersionOrigin
} from '../api/definitions-contracts';
import type { ISurveyVersion, IVersionDiff } from '../api/contracts';
import {
  AdminPage,
  LoadError,
  BUILDER_TABS,
  TableSkeleton,
  adminErrorMessage,
  useAdminData
} from '../components/admin-page';
import { versionPath, versionPreviewPath } from '../paths';
import { refusalMessage } from '../refusal';

/**
 * Survey versions — real, as of SR-123's first ticket, read through
 * `GET /api/v1/admin/definitions/surveys`. That one response carries every survey **and**
 * its versions, so the whole table is one request.
 *
 * ## Still read-only, and now for a different reason
 *
 * It used to be read-only because a fixture could not honour a publish. It can now: the
 * authoring surface has `POST /versions/{id}/validation`, `/publication` and `/retirement`,
 * all gated on `If-Match` against the draft's revision. They are deliberately not wired up
 * here. Publishing runs a validator and a pinned version is what a live response is frozen
 * against, so offering that button is a decision about staff workflow rather than a
 * transport detail — and it belongs to the authoring editor SR-123's remaining tickets are
 * for, not to a list.
 *
 * ## Columns: two lost, one recovered from elsewhere, two gained
 *
 * `vocabVersion` and `publishedBy` were predictions and neither is on the wire anywhere.
 * They are gone rather than shown as blanks.
 *
 * **"Pinned in" survived**, and not from the authoring surface: `GET /admin/cycles` carries
 * each cycle's `surveyVersionId`, so the pinning is a second read joined on that id. Worth
 * the extra request — it is the answer to "why does a retired version still matter", and a
 * pinned version is what a live response is frozen against.
 *
 * Two columns are new and real: **origin**, which says who wrote the version — a code
 * seeder, the authoring API, or the legacy migration, and four surveys are still seeded, so
 * that is the field telling them apart — and **revision**, the concurrency token every
 * mutation is gated on.
 *
 * The contents counts moved into the expandable row, because they come from the version
 * *document*: fetching it for every row to print three numbers would move the whole
 * definition of every version to render a table cell.
 */
export function AdminVersionsPage() {
  const { data: surveys, error, setError, reload } = useAdminData<IDefinitionSurvey[]>(
    (signal) => definitionsApi.surveys(signal)
  );

  // Which cycles pinned which version — the answer to "why does a retired version still
  // matter". It comes from the *oversight* catalogue rather than the authoring surface:
  // `GET /admin/cycles` carries each cycle's `surveyVersionId` and nothing under
  // `/definitions` does. Crossing surfaces is safe here because the page is admin-only and
  // admin satisfies both `responses.browse` and `definitions.author`; a staff-visible
  // version of this screen would have to check that again.
  //
  // Its own read, not part of `useAdminData` above, so a failure here costs the pinning
  // column and not the table.
  const [pins, setPins] = React.useState<Map<string, string[]>>(new Map());
  React.useEffect(() => {
    const controller = new AbortController();
    void oversightApi
      .cycles(undefined, controller.signal)
      .then((cycles) => {
        const byVersion = new Map<string, string[]>();
        for (const cycle of cycles) {
          const codes = byVersion.get(cycle.surveyVersionId) ?? [];
          codes.push(cycle.cycle.code);
          byVersion.set(cycle.surveyVersionId, codes);
        }
        setPins(byVersion);
      })
      .catch(() => {
        // Deliberately silent: the column degrades to "—", which is what an unpinned version
        // shows anyway, and a failed side read must not put an error banner over a table
        // that loaded perfectly well.
      });
    return () => controller.abort();
  }, []);

  const rows = React.useMemo(
    () =>
      (surveys ?? []).flatMap((survey) =>
        // Newest version first within a survey; the endpoint does not promise an order and a
        // list read top-down wants the current one at the top.
        [...survey.versions]
          .sort((left, right) => right.versionNo - left.versionNo)
          .map((version) => ({ survey, version }))
      ),
    [surveys]
  );

  return (
    <AdminPage title="Survey builder" tabs={BUILDER_TABS}>
      {error && <LoadError message={error} />}

      {surveys === null ? (
        <TableSkeleton />
      ) : (
        <>
          <NewDraft surveys={surveys} onCreated={reload} onFailed={setError} />

          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>Survey</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Written by</TableHead>
                  <TableHead>Revision</TableHead>
                  <TableHead>Pinned in</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ survey, version }) => (
                  <VersionRow
                    key={version.surveyVersionId}
                    surveyCode={survey.code}
                    surveyName={survey.name}
                    version={version}
                    pinnedIn={pins.get(version.surveyVersionId) ?? []}
                  />
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                      No survey has a version yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <VersionDiff />
        </>
      )}
    </AdminPage>
  );
}

const STATUS_VARIANT: Record<DefinitionVersionStatus, 'success' | 'soft' | 'outline'> = {
  published: 'success',
  draft: 'soft',
  retired: 'outline'
};

/**
 * `origin` in words. A `Record` rather than a ternary because there are **three** origins:
 * `legacy` is the reconstructed v0 definition the migration ETL's landing stage seeds, and
 * it appears in any database the migration has run against. A two-way check would have
 * quietly labelled those as authored.
 */
const ORIGIN_LABEL: Record<VersionOrigin, string> = {
  seed: 'a code seeder',
  authored: 'the authoring API',
  legacy: 'the legacy migration'
};

/**
 * One version, with its document loaded only when someone asks for it — and then kept, so
 * collapsing and re-expanding does not re-fetch a definition that cannot have changed
 * underneath a published version anyway.
 */
function VersionRow({
  surveyCode,
  surveyName,
  version,
  pinnedIn
}: {
  surveyCode: string;
  surveyName: string;
  version: IDefinitionVersion;
  pinnedIn: string[];
}) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [document, setDocument] = React.useState<Nullable<IVersionDocument>>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Nullable<string>>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next || document || loading) return;

    setLoading(true);
    setError(null);
    definitionsApi
      .version(version.surveyVersionId)
      .then(setDocument)
      .catch((loadError: unknown) =>
        setError(adminErrorMessage(loadError, 'Could not load this version'))
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <TableRow className="cursor-pointer" onClick={toggle}>
        <TableCell>
          {open ? (
            <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
          )}
          <span className="sr-only">{open ? 'Hide contents' : 'Show contents'}</span>
        </TableCell>
        <TableCell className="font-medium">
          {surveyCode}
          <span className="ml-2 text-xs text-muted-foreground">{surveyName}</span>
        </TableCell>
        <TableCell>v{version.versionNo}</TableCell>
        <TableCell>
          <Badge variant={STATUS_VARIANT[version.status]}>{version.status}</Badge>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{ORIGIN_LABEL[version.origin]}</TableCell>
        <TableCell className="text-xs text-muted-foreground">{version.revision}</TableCell>
        <TableCell className="text-xs">{pinnedIn.length === 0 ? '—' : pinnedIn.join(', ')}</TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {version.publishedAt ? formatDateTime(version.publishedAt) : 'not published'}
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(event: React.MouseEvent) => {
                // The row's own click toggles the summary; opening the editor is a different
                // intent and must not do both.
                event.stopPropagation();
                navigate(versionPath(version.surveyVersionId));
              }}
            >
              {version.status === 'draft' ? 'Edit' : 'Open'}
            </Button>
            {/* ADM-07. Pre-selects the first cycle this version is pinned in, because that
                is the one whose config a walkthrough should resolve against — and going
                without it silently swaps in an empty config. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                const cycle = pinnedIn[0] ? `?cycle=${encodeURIComponent(pinnedIn[0])}` : '';
                navigate(versionPreviewPath(version.surveyVersionId, cycle));
              }}
            >
              Preview
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {open && (
        <TableRow>
          <TableCell colSpan={9} className="bg-[var(--color-slight-blue)]/20">
            {loading && <span className="text-sm text-muted-foreground">Loading…</span>}
            {error && <LoadError message={error} />}
            {document && <VersionContents document={document} />}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function VersionContents({ document }: { document: IVersionDocument }) {
  return (
    <div className="grid gap-3 py-2">
      <p className="text-sm">
        {document.sections.length} sections · {document.usages.length} questions ·{' '}
        {document.rules.length} rules · {document.bindings.length} bindings
      </p>

      {document.notes && (
        <p className="text-sm text-muted-foreground">{document.notes}</p>
      )}

      <ol className="grid gap-1">
        {[...document.sections]
          .sort((left, right) => left.ordinal - right.ordinal)
          .map((section) => (
            <li key={section.sectionId} className="text-sm">
              <span className="font-mono text-xs text-muted-foreground">{section.code}</span>{' '}
              {section.title}
            </li>
          ))}
      </ol>
    </div>
  );
}

/**
 * The one thing on this screen still standing on a fixture — and the only one on the whole
 * admin section that is not merely *waiting* for an endpoint but has none proposed.
 *
 * A cross-version diff is a real need (the design names `question_id` as the diff and
 * migration key, §3.4) and no route answers it. Rather than dropping the panel, it keeps its
 * fixture and says so on itself: this is the shape the endpoint should answer with, which is
 * more useful than an empty space.
 *
 * **It reads the fixture's own version numbers, not the real list above**, and that is the
 * point rather than an oversight. The two sets overlap without either containing the other:
 * the fixture has RIA_FIRM v1–v3 and one version each of FA, FSP and Teams; survey-api has
 * RIA_FIRM v1–v2 and v1–v3 for the rest. Feeding real numbers into a mock computation would
 * lose the only diff the fixture can compute and offer FA v2→v3, which it would answer by
 * calling every FA question removed — output that looks like a finding and is an artifact. A
 * visibly self-contained mock is the honest version. See `adminApi.diffableVersions`.
 *
 * The diff is keyed by bank question, not by usage code: a usage can be re-lettered between
 * versions while the question stays the same.
 */
function VersionDiff() {
  const { data: versions } = useAdminData<ISurveyVersion[]>(() => adminApi.diffableVersions());

  const surveys = React.useMemo(
    () =>
      Array.from(new Set((versions ?? []).map((version) => version.surveyCode))).filter(
        (code) => (versions ?? []).filter((version) => version.surveyCode === code).length > 1
      ),
    [versions]
  );

  const [surveyCode, setSurveyCode] = React.useState('');
  const numbers = React.useMemo(
    () =>
      (versions ?? [])
        .filter((version) => version.surveyCode === surveyCode)
        .map((version) => version.versionNo)
        .sort((left, right) => right - left),
    [versions, surveyCode]
  );

  const [to, setTo] = React.useState<number | undefined>(undefined);
  const [from, setFrom] = React.useState<number | undefined>(undefined);
  const [diff, setDiff] = React.useState<Nullable<IVersionDiff>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  // Seeded once the fixture arrives, and re-seeded when the survey changes: version numbers
  // are the survey's own.
  React.useEffect(() => {
    if (!surveyCode && surveys[0]) setSurveyCode(surveys[0]);
  }, [surveys, surveyCode]);

  React.useEffect(() => {
    setTo(numbers[0]);
    setFrom(numbers[1]);
  }, [numbers]);

  React.useEffect(() => {
    if (!surveyCode || from === undefined || to === undefined) return;
    let live = true;
    void adminApi
      .diffVersions(surveyCode, from, to)
      .then((result) => live && setDiff(result))
      .catch((diffError: unknown) => {
        if (live) setError(adminErrorMessage(diffError, 'Could not diff'));
      });
    return () => {
      live = false;
    };
  }, [surveyCode, from, to]);

  if (versions !== null && surveys.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Compare versions</CardTitle>
          <CardDescription>
            No survey here has two versions yet, so there is nothing to compare.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Compare versions</CardTitle>
        <CardDescription>
          Matched by bank question rather than by field ID, so a re-lettered question reads as
          a change instead of a removal and an addition.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Alert>
          <AlertTitle>This panel is still mocked</AlertTitle>
          <AlertDescription>
            There is no diff endpoint — not pending, not proposed. The version list
            above is real; what this card shows comes from an in-memory fixture and reaches no
            database.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap gap-3">
          <Select value={surveyCode} onValueChange={setSurveyCode}>
            <SelectTrigger className="w-48" aria-label="Survey">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {surveys.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(from ?? '')} onValueChange={(next) => setFrom(Number(next))}>
            <SelectTrigger className="w-32" aria-label="From version">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {numbers.map((number) => (
                <SelectItem key={number} value={String(number)}>
                  v{number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(to ?? '')} onValueChange={(next) => setTo(Number(next))}>
            <SelectTrigger className="w-32" aria-label="To version">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {numbers.map((number) => (
                <SelectItem key={number} value={String(number)}>
                  v{number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && <LoadError message={error} />}

        {diff && (
          <div className="grid gap-4">
            <DiffGroup
              title={`Added in v${diff.to}`}
              rows={diff.added.map((entry) => ({
                key: entry.questionCode,
                left: entry.questionCode,
                right: `${entry.usageCode} · ${entry.sectionCode} · ${entry.label}`
              }))}
            />
            <DiffGroup
              title={`Gone from v${diff.to}`}
              rows={diff.removed.map((entry) => ({
                key: entry.questionCode,
                left: entry.questionCode,
                right: `${entry.usageCode} · ${entry.sectionCode} · ${entry.label}`
              }))}
            />
            <DiffGroup
              title="Changed"
              rows={diff.changed.map((change) => ({
                key: `${change.questionCode}-${change.field}`,
                left: `${change.questionCode} · ${change.field}`,
                right: `${change.before} → ${change.after}`
              }))}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DiffGroup({
  title,
  rows
}: {
  title: string;
  rows: { key: string; left: string; right: string }[];
}) {
  return (
    <div className="grid gap-1">
      <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {title} ({rows.length})
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing.</p>
      ) : (
        rows.map((row) => (
          <div key={row.key} className="grid gap-0.5 rounded-md border border-border px-3 py-2">
            <span className="font-mono text-xs">{row.left}</span>
            <span className="text-sm">{row.right}</span>
          </div>
        ))
      )}
    </div>
  );
}

/**
 * Cutting a new draft — the entry point to the editor, and the only version write that does
 * not live inside it.
 *
 * `source` is required by the server: `clone` copies an existing version's sections, usages
 * and rules, `empty` starts with nothing. Cloning is offered from the newest version because
 * that is what "the next cycle's questionnaire" almost always means — and worth knowing
 * before doing it, a cloned draft **inherits nothing from the bank**: every column it copies
 * is materialized, which is why the editor shows what is still inherited and what is not.
 *
 * A survey may hold only one draft at a time, so the control says so rather than letting the
 * server refuse.
 */
function NewDraft({
  surveys,
  onCreated,
  onFailed
}: {
  surveys: IDefinitionSurvey[];
  onCreated: () => void;
  onFailed: (message: string) => void;
}) {
  const navigate = useNavigate();
  const [surveyCode, setSurveyCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const survey = surveys.find((candidate) => candidate.code === surveyCode);
  const newest = [...(survey?.versions ?? [])].sort(
    (left, right) => right.versionNo - left.versionNo
  )[0];
  const hasDraft = (survey?.versions ?? []).some((version) => version.status === 'draft');

  const cut = async (source: 'empty' | 'clone') => {
    if (!survey) return;
    setBusy(true);
    try {
      const created = await definitionsApi.createVersion(survey.code, {
        source,
        fromVersion: source === 'clone' ? newest?.versionNo : undefined
      });
      onCreated();
      navigate(versionPath(created.surveyVersionId));
    } catch (createError) {
      onFailed(refusalMessage(createError, 'Could not cut a draft'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <Select value={surveyCode} onValueChange={setSurveyCode}>
        <SelectTrigger className="w-56" aria-label="Survey to draft">
          <SelectValue placeholder="Cut a draft for…" />
        </SelectTrigger>
        <SelectContent>
          {surveys.map((candidate) => (
            <SelectItem key={candidate.code} value={candidate.code}>
              {candidate.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button disabled={busy || !survey || hasDraft || !newest} onClick={() => void cut('clone')}>
        <Plus />
        {newest ? `Clone v${newest.versionNo}` : 'Clone the newest'}
      </Button>
      <Button
        variant="outline"
        disabled={busy || !survey || hasDraft}
        onClick={() => void cut('empty')}
      >
        Start empty
      </Button>

      {hasDraft && (
        <span className="text-sm text-muted-foreground">
          {surveyCode} already has a draft — open it from the table.
        </span>
      )}
    </div>
  );
}
